import { Tabs } from "@base-ui/react/tabs";
import type React from "react";
import { forwardRef } from "react";
import { SingleTab } from "../SingleTab";
import {
  tabGroupListClass,
  tabGroupWrapperClass,
  transparentIndicatorClass,
} from "./styles.css";

interface TabArrayItemProps {
  text: string;
  icon?: React.ReactNode;
  counter?: number;
}

export type TabGroupVariant = "default" | "transparent";

interface TabGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue"> {
  variant?: TabGroupVariant;
  tabs: (TabArrayItemProps | string)[];
  defaultTab?: number;
  /** Called when the active tab changes. Follows Base UI `onValueChange` convention. */
  onValueChange?: (tabIndex: number) => void;
  value?: number;
  tabStyles?: React.CSSProperties;
}

export const TabGroup = forwardRef(function TabGroup(
  {
    variant = "default",
    tabs,
    defaultTab = 0,
    onValueChange,
    value,
    tabStyles,
    ...props
  }: TabGroupProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <Tabs.Root
      ref={ref}
      className={tabGroupWrapperClass}
      defaultValue={defaultTab}
      value={value}
      onValueChange={(tabValue) => {
        onValueChange?.(tabValue as number);
      }}
      data-slot="tab-group"
      {...props}
    >
      <Tabs.List className={tabGroupListClass} data-slot="tab-list">
        {Array.isArray(tabs) &&
          tabs.map((tab, idx) => {
            if (typeof tab === "string") {
              return (
                <SingleTab
                  key={idx}
                  style={tabStyles}
                  value={idx}
                  variant={variant}
                >
                  {tab}
                </SingleTab>
              );
            }
            return (
              <SingleTab
                key={idx}
                style={tabStyles}
                value={idx}
                variant={variant}
                icon={tab.icon}
                counter={tab.counter}
              >
                {tab.text}
              </SingleTab>
            );
          })}
        {variant === "transparent" && (
          <Tabs.Indicator
            className={transparentIndicatorClass}
            data-slot="tab-indicator"
          />
        )}
      </Tabs.List>
    </Tabs.Root>
  );
});

TabGroup.displayName = "TabGroup";
